<template>
  <div class="flex min-h-[300px] relative">
    <canvas
      ref="chartCanvas"
      width="100%"
      height="100%"
    />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, nextTick, getCurrentInstance } from 'vue'
import { Chart, registerables } from 'chart.js'

// Color palette for per-reward lines
const REWARD_COLORS = [
  '#FFCB68', '#29CB00', '#FF6B6B', '#4ECDC4',
  '#A78BFA', '#F97316', '#06B6D4', '#EC4899',
  '#84CC16', '#6366F1'
]

// Fixed colors for totals
const TOTAL_AMOUNT_COLOR = '#2BDEE9'
const TOTAL_COUNT_COLOR = '#DC49D6'

export default {
  name: 'CampaignDashboardChart',
  props: {
    chartData: {
      type: Object,
      default: () => ({ rewards: [] })
    },
    startAt: {
      type: String,
      required: true
    },
    endAt: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const instance = getCurrentInstance()
    const $t = instance?.proxy?.$t || ((key) => key)

    const chartCanvas = ref(null)
    let chart = null
    let isMounted = false

    const getDateRange = (startDate, endDate) => {
      const dates = []
      if (!startDate || !endDate) return dates
      let current = new Date(startDate.replace(/\//g, '-'))
      const stop = new Date(endDate.replace(/\//g, '-'))
      while (current <= stop) {
        dates.push(current.toISOString().split('T')[0])
        current.setDate(current.getDate() + 1)
      }
      return dates
    }

    const chartConfigOptions = {
      maintainAspectRatio: false,
      responsive: true,
      scales: {
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          grid: {
            display: true,
            drawBorder: true,
            borderColor: '#3E434A',
            color: '#3E434A',
            borderWidth: 1
          },
          ticks: {
            beginAtZero: true,
            color: 'white',
            callback: function(value) {
              if (Math.floor(value) === value) return value
            }
          },
          title: {
            display: true,
            text: $t('campaigns.dashboard.chart_axis.amount'),
            color: '#99A0A8'
          }
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          grid: {
            drawOnChartArea: false
          },
          ticks: {
            beginAtZero: true,
            color: 'white',
            callback: function(value) {
              if (Math.floor(value) === value) return value
            }
          },
          title: {
            display: true,
            text: $t('campaigns.dashboard.chart_axis.quantity'),
            color: '#99A0A8'
          }
        },
        x: {
          grid: {
            display: true,
            color: '#3E434A',
            borderColor: '#3E434A',
            borderDash: [6, 3],
            borderWidth: 1
          },
          ticks: {
            color: 'white',
            autoSkip: true,
            maxTicksLimit: 7,
            maxRotation: 0,
            minRotation: 0
          }
        }
      },
      plugins: {
        legend: {
          display: true,
          position: 'top',
          align: 'end',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            color: '#99A0A8',
            boxWidth: 5,
            boxHeight: 5
          }
        },
        tooltip: {
          titleAlign: 'center',
          usePointStyle: true,
          padding: 10,
          bodySpacing: 8,
          boxWidth: 5,
          boxHeight: 5,
          boxPadding: 5,
          callbacks: {
            label: function(context) {
              const label = context.dataset.label || ''
              const value = context.parsed.y !== null ? context.parsed.y : ''
              if (context.dataset.yAxisID === 'y') {
                return `${label}: $${Number(value).toFixed(2)}`
              }
              return `${label}: ${value}`
            }
          }
        }
      },
      interaction: {
        mode: 'index',
        intersect: false
      }
    }

    const initializeChart = async () => {
      if (!isMounted) return
      await nextTick()

      if (!chartCanvas.value) {
        setTimeout(async () => {
          if (!isMounted) return
          await nextTick()
          if (chartCanvas.value) initializeChartInstance()
        }, 100)
        return
      }
      initializeChartInstance()
    }

    const initializeChartInstance = () => {
      if (!isMounted || !chartCanvas.value) return

      Chart.register(...registerables)

      chart = new Chart(chartCanvas.value, {
        type: 'line',
        data: { labels: [], datasets: [] },
        options: chartConfigOptions
      })
    }

    const updateChart = () => {
      if (!chart || !props.chartData) return

      const dateLabels = getDateRange(props.startAt, props.endAt)
      const rewards = props.chartData.rewards || []
      const datasets = []

      // Aggregate totals across all rewards per date
      const totalAmountByDate = {}
      const totalCountByDate = {}

      dateLabels.forEach(date => {
        const key = date.replace(/-/g, '/')
        let amount = 0
        let count = 0
        rewards.forEach(reward => {
          const daily = reward.daily || {}
          if (daily[key]) {
            amount += daily[key].amount || 0
            count += daily[key].count || 0
          }
        })
        totalAmountByDate[date] = amount
        totalCountByDate[date] = count
      })

      // 1. Total Sales Amount (left Y axis, solid thick line)
      datasets.push({
        label: $t('campaigns.dashboard.chart_legend.total_sales'),
        data: dateLabels.map(d => totalAmountByDate[d]),
        borderColor: TOTAL_AMOUNT_COLOR,
        backgroundColor: TOTAL_AMOUNT_COLOR,
        yAxisID: 'y',
        fill: false,
        tension: 0.1,
        pointRadius: 3,
        pointBackgroundColor: TOTAL_AMOUNT_COLOR,
        pointBorderColor: '#fff',
        pointHoverRadius: 5,
        borderWidth: 2.5
      })

      // 2. Total Units Sold (right Y axis, solid thick line)
      datasets.push({
        label: $t('campaigns.dashboard.chart_legend.total_units'),
        data: dateLabels.map(d => totalCountByDate[d]),
        borderColor: TOTAL_COUNT_COLOR,
        backgroundColor: TOTAL_COUNT_COLOR,
        yAxisID: 'y1',
        fill: false,
        tension: 0.1,
        pointRadius: 3,
        pointBackgroundColor: TOTAL_COUNT_COLOR,
        pointBorderColor: '#fff',
        pointHoverRadius: 5,
        borderWidth: 2.5
      })

      // 3. Per-reward unit count (right Y axis, dashed thin lines)
      rewards.forEach((reward, index) => {
        const color = REWARD_COLORS[index % REWARD_COLORS.length]
        const daily = reward.daily || {}

        const countData = dateLabels.map(date => {
          const key = date.replace(/-/g, '/')
          return daily[key]?.count || 0
        })

        datasets.push({
          label: reward.reward_title,
          data: countData,
          borderColor: color,
          backgroundColor: color,
          yAxisID: 'y1',
          fill: false,
          tension: 0.1,
          pointRadius: 2,
          pointBackgroundColor: color,
          pointBorderColor: '#fff',
          pointHoverRadius: 4,
          borderWidth: 1.5,
          borderDash: [5, 3]
        })
      })

      chart.data.labels = dateLabels
      chart.data.datasets = datasets
      chart.update('active')
    }

    const resizeChart = () => {
      if (chart) chart.resize()
    }

    onMounted(() => {
      isMounted = true
      initializeChart().then(() => {
        if (chart) updateChart()
      })
      window.addEventListener('resize', resizeChart)
    })

    onUnmounted(() => {
      isMounted = false
      if (chart) {
        chart.destroy()
        chart = null
      }
      window.removeEventListener('resize', resizeChart)
    })

    watch(
      () => [props.chartData, props.startAt, props.endAt],
      () => {
        if (chart) updateChart()
      },
      { deep: true }
    )

    return { chartCanvas }
  }
}
</script>
